import { useCallback, useEffect } from 'react';
import { apiService, chatApiService } from 'infrastructure/services/ApiService';
import { errorService } from 'infrastructure/services/ErrorService';
import { ApiMethodsEnum } from 'infrastructure/enums/ApiMethodsEnum';
import { useSafeState } from './useSafeState';

export const useRequest = ({
  url = '',
  method = ApiMethodsEnum.Get,
  sendOnStart = false,
  body = {},
  polling = 0, // in seconds
  isCancelMode = false,
  chatService = false,
}) => {
  const [status, setStatus] = useSafeState(null);
  const [data, setData] = useSafeState(null);
  const [isLoading, setIsLoading] = useSafeState(false);
  const [error, setError] = useSafeState(null);
  const [counter, setCounter] = useSafeState(polling);
  const [cancelSource, setCancelSource] = useSafeState('');

  const serviceInstance = chatService ? chatApiService : apiService;

  const send = useCallback(
    // eslint-disable-next-line consistent-return
    async (additionalBody = {}, urlWithParams = '') => {
      setIsLoading(true);
      setStatus(null);
      setError(null);

      let source = cancelSource;

      if (isCancelMode) {
        if (source) source.cancel('Request is canceled.');
        // @ts-ignore
        source = serviceInstance.getSource();
        setCancelSource(source);
      }

      try {
        // Handle FormData separately to preserve multipart/form-data
        const requestData =
          additionalBody instanceof FormData ? additionalBody : { ...body, ...additionalBody };

        const response = await serviceInstance({
          url: urlWithParams || url,
          method,
          data: requestData,
          cancelToken: source?.token,
        });

        setData(response.data);
        setStatus(response.status);
        setError(null);
        return response.data;
      } catch (e: any) {
        setError(e);
        if (e.response?.status) {
          setStatus(e.response.status);

          if (e.response.status === 500) {
            errorService.send(e);
          }
        } else if (e.code !== 'ERR_CANCELED') {
          errorService.send(e);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [body, url, method, isCancelMode, cancelSource]
  );

  useEffect(() => {
    if (!sendOnStart) return;

    send();
  }, [sendOnStart]);

  useEffect(() => {
    if (!polling) return undefined;

    const timer = setTimeout(() => {
      if (counter <= 1) {
        setCounter(polling);
      } else {
        setCounter(counter - 1);
      }

      if (counter === 1) send();
    }, 1000);

    return () => {
      clearTimeout(timer);
    };
  }, [counter, polling]);

  return { data, isLoading, error, send, counter, status };
};
